/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { Button, Dropdown, Menu, Skeleton, Tooltip } from 'antd';

import {
  CommentOutlined,
  CopyOutlined,
  EditOutlined,
  ExportOutlined,
  FileProtectOutlined,
  FileTextOutlined,
  MailOutlined,
  WarningOutlined,
} from '@ant-design/icons';

import Utils from '../../../Assets/Scripts/Utils';
import EmailConfirmPopup from '../../../Components/EmailConfirmPopup/EmailConfirmPopup';
import OccurrencesPopup from '../../../Components/OccurrencePopup/OccurrencePopup';
import { createShowField } from '../../../Components/ResourceFieldLayout/shouldRenderTaskField';
import CertificateSettingsModal from '../../Certificate/Components/CertificateSettingsModal';
import GenerateCertificateModal from '../../Certificate/Components/GenerateCertificateModal';
import UploadStudentsModal from '../../Certificate/Components/UploadStudentsModal';
import { shouldShowOsWorkOrderButton } from '../osWorkOrderCatalog';

import OsWorkOrderDrawer from './OsWorkOrderDrawer';

import './TaskDrawerExtra.scss';

function TaskDrawerExtraComponent({
  isLoading,
  fieldsPermissions,
  form,
  task,
  isTaskReadOnly,
  setIsEditing,
  certificateModelOptions,
  productOptions,
  handleTaskUpdate,
  setComments,
  page,
  scheduleType,
  setShowModalIntercurrence,
  fieldLayout,
  roleId,
}) {
  const showField = createShowField(fieldsPermissions, fieldLayout, roleId);
  const [occurrencesPopupVisible, setOccurrencesPopupVisible] = useState(false);
  const [emailConfirmPopupVisible, setEmailConfirmPopupVisible] = useState(false);
  const [confirmEmailType, setConfirmEmailType] = useState(null);
  const [isCertificateSettingsModalOpen, setIsCertificateSettingsModalOpen] = useState(false);
  const [isUploadStudentsModalOpen, setIsUploadStudentsModalOpen] = useState(false);
  const [isGenerateCertificateModalOpen, setIsGenerateCertificateModalOpen] = useState(false);
  const [isOsDrawerOpen, setIsOsDrawerOpen] = useState(false);
  const [isDownloadingSelection, setIsDownloadingSelection] = useState(false);
  const showOsWorkOrderButton = shouldShowOsWorkOrderButton({ scheduleType, page });

  const onOsButtonClick = () => {
    if (isDownloadingSelection) return;
    setIsOsDrawerOpen(true);
  };

  const openEmailConfirmPopup = (type) => {
    setConfirmEmailType(type);
    setEmailConfirmPopupVisible(true);
  };

  // Extrair dados do task para usar nas condições
  const stageId = task.stage?.id;
  const isInCompany = task.trainingType === 'In Company';
  const hasInstructor = task.mainInstructor?.userId;
  const hasTransport = task.trainingTransportation === 'Incluso';
  const hasDriverVehicleTransport =
    task.transportCompany?.id && task.driverList && task.driverList.length > 0;
  const isDriverVehicleSameSize = task.driverList?.every((driver) => driver.vehicle) || false;
  const invalidStages = [1, 6, 10, 12]; // 1 = Aguardando, 6 = Reagendar, 10 = Pré-Reservado, 12 = Reservado
  const isValidStage = !invalidStages.includes(stageId);

  const shouldDisableCertificateGeneration = () => {
    const hasAccess = fieldsPermissions?.GenerateCertificateBtn?.access >= 1;
    const hasStudents = task.studentList?.length > 0 && task.queryStudentList?.length > 0;
    return !task.certificateModel || !hasStudents || !hasAccess || !isValidStage || isTaskReadOnly;
  };

  const getTaskOperatorStage = () => {
    let driverStage;
    switch (scheduleType) {
      case 'mainInstructor':
        return task?.mainInstructor?.stage?.id;
      case 'assistantInstructor':
        return task?.assistantInstructor?.stage?.id;
      case 'driver': {
        const driverUser = JSON.parse(localStorage.getItem('conecta__userData'));

        task?.driverList?.forEach((driver) => {
          if (driver.userId === driverUser.id) {
            driverStage = driver.stage.id;
          }
        });
        return driverStage;
      }
      default:
        return 0;
    }
  };

  const currentOperatorStageId = getTaskOperatorStage();

  let showEditBtn = !isTaskReadOnly;
  let showIntercurrenceBtn = false;

  if (scheduleType) {
    showEditBtn = !isTaskReadOnly && currentOperatorStageId !== 1;
    showIntercurrenceBtn = !isTaskReadOnly && currentOperatorStageId !== 1;
  }

  const emailMenu = (
    <Menu>
      <Menu.Item
        key="1"
        onClick={() => openEmailConfirmPopup(1)}
        disabled={fieldsPermissions?.EmailConfirmTrainingButton?.access < 1 || !isValidStage}
      >
        Enviar E-mail Confirmação Treinamento
      </Menu.Item>
      <Menu.Item
        key="2"
        onClick={() => openEmailConfirmPopup(2)}
        disabled={
          fieldsPermissions?.EmailConfirmTransportButton?.access < 1 ||
          !isValidStage ||
          !hasDriverVehicleTransport ||
          !hasTransport
        }
      >
        Enviar E-mail Confirmação Transporte
      </Menu.Item>
      <Menu.Item
        key="3"
        onClick={() => openEmailConfirmPopup(3)}
        disabled={
          fieldsPermissions?.EmailConfirmInstructorButton?.access < 1 ||
          !isValidStage ||
          !isInCompany ||
          !hasInstructor
        }
      >
        Enviar E-mail Confirmação Instrutor
      </Menu.Item>
      <Menu.Item
        key="4"
        onClick={() => openEmailConfirmPopup(4)}
        disabled={
          fieldsPermissions?.EmailConfirmDriverButton?.access < 1 ||
          !isValidStage ||
          !hasDriverVehicleTransport ||
          !isDriverVehicleSameSize ||
          !hasTransport
        }
      >
        Enviar E-mail Confirmação Motorista
      </Menu.Item>
    </Menu>
  );

  const certificateMenu = (
    <Menu>
      {showField('CertificateTrainingSettingsBtn') && (
        <Menu.Item
          key="1"
          onClick={() => setIsUploadStudentsModalOpen(true)}
          disabled={
            fieldsPermissions?.CertificateTrainingSettingsBtn?.access < 1 ||
            !isValidStage ||
            isTaskReadOnly
          }
        >
          Importar Lista de Alunos
        </Menu.Item>
      )}

      {showField('ImportStudentListBtn') && (
        <Menu.Item
          key="2"
          onClick={() => setIsCertificateSettingsModalOpen(true)}
          disabled={
            fieldsPermissions?.ImportStudentListBtn?.access < 1 || !isValidStage || isTaskReadOnly
          }
        >
          Configurar Certificado
        </Menu.Item>
      )}

      {showField('GenerateCertificateBtn') && (
        <Menu.Item
          key="3"
          onClick={() =>
            setIsGenerateCertificateModalOpen('certificateWithStudents', form.getFieldValue('id'))
          }
          disabled={shouldDisableCertificateGeneration()}
        >
          Gerar Certificado
        </Menu.Item>
      )}
    </Menu>
  );

  if (isLoading) {
    return (
      <div className="task-drawer-skeleton-wrapper">
        {showOsWorkOrderButton && (
          <Skeleton.Avatar style={{ margin: 0 }} active={true} size="default" shape="circle" />
        )}
        <Skeleton.Avatar style={{ margin: 0 }} active={true} size="default" shape="circle" />
        <Skeleton.Avatar style={{ margin: 0 }} active={true} size="default" shape="circle" />
        <Skeleton.Avatar style={{ margin: 0 }} active={true} size="default" shape="circle" />
        <Skeleton.Avatar style={{ margin: 0 }} active={true} size="default" shape="circle" />
        <Skeleton.Avatar style={{ margin: 0 }} active={true} size="default" shape="circle" />
      </div>
    );
  }

  if (!isLoading) {
    return (
      <>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* Não exibe botão caso esteja em modo Somente Visualização ou Estágio seja igual a Concluído */}
          {showEditBtn && (
            <Tooltip title="Editar">
              <Button onClick={() => setIsEditing(true)} icon={<EditOutlined />} />
            </Tooltip>
          )}

          {(showField('EmailConfirmTrainingButton') ||
            showField('EmailConfirmTransportButton') ||
            showField('EmailConfirmInstructorButton') ||
            showField('EmailConfirmDriverButton')) &&
            !isTaskReadOnly && (
              <Dropdown
                overlay={emailMenu}
                disabled={isLoading || task?.stage?.id === 5}
                trigger={['hover']}
              >
                <Button icon={<MailOutlined />} />
              </Dropdown>
            )}

          {(showField('CertificateTrainingSettingsBtn') ||
            showField('ImportStudentListBtn') ||
            showField('GenerateCertificateBtn')) &&
            !isTaskReadOnly && (
              <Dropdown
                overlay={certificateMenu}
                disabled={isLoading || task?.stage?.id === 5}
                trigger={['hover']}
              >
                <Button icon={<FileProtectOutlined />} />
              </Dropdown>
            )}

          {showField('OccurrenceButton') && !isTaskReadOnly && (
            <Tooltip title="Adicionar Ocorrência">
              <Button
                onClick={() => setOccurrencesPopupVisible(true)}
                icon={<CommentOutlined />}
                disabled={isLoading || task?.stage?.id === 5}
              />
            </Tooltip>
          )}

          {showIntercurrenceBtn && (
            <Tooltip title="Adicionar Intercorrência">
              <Button onClick={() => setShowModalIntercurrence(true)} icon={<WarningOutlined />} />
            </Tooltip>
          )}

          <Tooltip title="Copiar Detalhes do Treinamento">
            <Button onClick={(e) => Utils.onCopyDetails(task, e)} icon={<CopyOutlined />} />
          </Tooltip>

          {showOsWorkOrderButton && (
            <Tooltip title="Ordem de Serviço">
              <Button
                onClick={onOsButtonClick}
                loading={isDownloadingSelection}
                icon={<FileTextOutlined />}
              />
            </Tooltip>
          )}

          {page !== 'ScheduleTrainingCenter' && !scheduleType && (
            <Tooltip title="Abrir no Bitrix24">
              <Button
                onClick={() =>
                  window.open(
                    `https://conecta.bitrix24.com.br/crm/deal/details/${task.dealId}/`,
                    '_blank'
                  )
                }
                icon={<ExportOutlined />}
              />
            </Tooltip>
          )}
        </div>

        <OccurrencesPopup
          isVisible={occurrencesPopupVisible}
          onClose={() => setOccurrencesPopupVisible(false)}
          currentOpenTask={task}
          setComments={setComments}
          task={task}
        />

        {emailConfirmPopupVisible && (
          <EmailConfirmPopup
            onClose={() => setEmailConfirmPopupVisible(false)}
            currentOpenTask={task}
            type={confirmEmailType}
          />
        )}

        {isCertificateSettingsModalOpen && (
          <CertificateSettingsModal
            form={form}
            setIsOpen={setIsCertificateSettingsModalOpen}
            certificateModelOptions={certificateModelOptions}
            productOptions={productOptions}
            taskList={[task]}
            selectedRowKeys={[task.id]}
            isSchedulingScreen={true}
            handleTaskUpdate={handleTaskUpdate}
          />
        )}

        {isUploadStudentsModalOpen && (
          <UploadStudentsModal
            form={form}
            setIsOpen={setIsUploadStudentsModalOpen}
            taskList={[task]}
            selectedRowKeys={[task.id]}
            isSchedulingScreen={true}
            handleTaskUpdate={handleTaskUpdate}
          />
        )}

        {isGenerateCertificateModalOpen && (
          <GenerateCertificateModal
            setIsOpen={setIsGenerateCertificateModalOpen}
            taskList={[task]}
            selectedTaskIds={[task.id]}
            showTable={true}
          />
        )}

        <OsWorkOrderDrawer
          open={isOsDrawerOpen}
          task={task}
          form={form}
          isDownloadingSelection={isDownloadingSelection}
          onClose={() => setIsOsDrawerOpen(false)}
          onDownloadSelectionChange={setIsDownloadingSelection}
        />
      </>
    );
  }
}

export default TaskDrawerExtraComponent;
